import type { TickRendererProps } from '@visx/axis';

import { useTickTruncation } from './hooks/useTickTruncation.js';
import { useXTickOffset } from './hooks/useXTickOffset.js';
import { TickWithTruncationForeignObject } from './TickWithTruncationForeignObject.js';
import { X_AXES_TICK_LINE_OFFSET } from '../../../core/components/axes/constants.js';
import { useXYChartTruncationMode } from '../../hooks/useXYChartTruncationMode.js';

interface YTickWithTruncationProps {
  position: 'top' | 'bottom';
  renderProps: TickRendererProps;
  maxSpaceForTruncation: number;
  centerTick: boolean;
  isTickRotated: boolean;
  tickNameHasEmoji: boolean;
}

export function XTickWithTruncation({
  position,
  renderProps,
  maxSpaceForTruncation,
  centerTick,
  isTickRotated,
  tickNameHasEmoji,
}: Readonly<YTickWithTruncationProps>) {
  const { formattedValue, x: xFromProps, y: yFromProps } = renderProps;

  const { truncationMode } = useXYChartTruncationMode();

  const {
    displayValue,
    tickClassName,
    tickInlineStyle,
    truncatedHeight,
    truncatedWidth,
    truncatedValue,
  } = useTickTruncation(formattedValue, maxSpaceForTruncation, truncationMode);

  const { xOffset, yOffset, style } = useXTickOffset(
    truncatedWidth,
    truncatedHeight,
    renderProps,
    centerTick,
    isTickRotated,
    position,
    tickNameHasEmoji,
  );

  const showTooltip = truncatedValue === formattedValue;

  const x = isTickRotated
    ? position === 'top'
      ? X_AXES_TICK_LINE_OFFSET
      : -(truncatedWidth + X_AXES_TICK_LINE_OFFSET)
    : xFromProps - xOffset;

  const y = isTickRotated
    ? xFromProps - (centerTick ? yOffset : xOffset)
    : yFromProps - yOffset;

  return (
    <TickWithTruncationForeignObject
      x={x}
      y={y}
      style={style}
      height={style.height as number}
      width={style.width as number}
      tickClassName={tickClassName}
      tickInlineStyle={tickInlineStyle}
      formattedValue={formattedValue as string}
      displayValue={displayValue}
      showTooltip={showTooltip}
    />
  );
}
